<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import KaptionAssistantSelector from './KaptionAssistantSelector.vue';
import ScopeSelector from './ScopeSelector.vue';
import TokenCreatedModal from './TokenCreatedModal.vue';

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    KaptionAssistantSelector,
    ScopeSelector,
    TokenCreatedModal,
  },
  data() {
    return {
      name: '',
      // 'me' → a personal key owned by the current user; 'assistant' → the
      // historical shape, owned by an assistant user.
      ownerMode: 'me',
      kaptionAssistantId: null,
      selectedRoles: [],
      allAccounts: false,
      selectedAccountIds: [],
      selectedScopes: [],
      expiresIn: '90_days',
      showTokenModal: false,
      createdToken: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'kaptionAccessTokens/getUIFlags',
      kaptionAssistants: 'kaptionAccessTokens/getKaptionAssistants',
      scopes: 'kaptionAccessTokens/getScopes',
      roles: 'kaptionAccessTokens/getRoles',
      accounts: 'kaptionAccessTokens/getAccounts',
      currentUser: 'getCurrentUser',
    }),
    roleRows() {
      return Object.entries(this.roles).map(([key, role]) => ({
        key,
        label: role.label,
        description: role.description,
      }));
    },
    // Scopes the chosen roles bundle, shown alongside manual picks.
    effectiveScopes() {
      const out = new Set(this.selectedScopes);
      this.selectedRoles.forEach(key => {
        (this.roles[key]?.scopes || []).forEach(scope => out.add(scope));
      });
      return [...out];
    },
    hasAccountChoice() {
      return this.allAccounts || this.selectedAccountIds.length > 0;
    },
    expirationOptions() {
      return [
        {
          value: '7_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_7'),
        },
        {
          value: '30_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_30'),
        },
        {
          value: '60_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_60'),
        },
        {
          value: '90_days',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.DAYS_90'),
        },
        {
          value: '1_year',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.YEAR_1'),
        },
        {
          value: 'never',
          label: this.$t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.NEVER'),
        },
      ];
    },
    isFormValid() {
      const ownerChosen =
        this.ownerMode === 'me' || this.kaptionAssistantId !== null;
      return (
        this.name.trim() !== '' &&
        ownerChosen &&
        this.effectiveScopes.length > 0 &&
        (this.ownerMode !== 'me' || this.hasAccountChoice)
      );
    },
  },
  mounted() {
    this.$store.dispatch('kaptionAccessTokens/fetchKaptionAssistants');
    this.$store.dispatch('kaptionAccessTokens/fetchScopes');
    this.$store.dispatch('kaptionAccessTokens/fetchRoles');
    // A personal key covers at least the workspace it is created in.
    this.selectedAccountIds = [this.currentUser.account_id];
  },
  methods: {
    onAssistantSelect(assistant) {
      this.kaptionAssistantId = assistant?.id || null;
    },
    onScopesChange(scopes) {
      this.selectedScopes = scopes;
    },
    toggleRole(key) {
      this.selectedRoles = this.selectedRoles.includes(key)
        ? this.selectedRoles.filter(k => k !== key)
        : [...this.selectedRoles, key];
    },
    accountLabel(account) {
      return `${account.name} (${account.role})`;
    },
    toggleAccount(id) {
      this.selectedAccountIds = this.selectedAccountIds.includes(id)
        ? this.selectedAccountIds.filter(a => a !== id)
        : [...this.selectedAccountIds, id];
    },
    navigateBack() {
      this.$router.push({
        name: 'settings_kaption_access_tokens',
      });
    },
    hideTokenModal() {
      this.showTokenModal = false;
      this.navigateBack();
    },
    async createToken() {
      if (!this.isFormValid) return;

      try {
        const response = await this.$store.dispatch(
          'kaptionAccessTokens/create',
          {
            kaption_access_token: {
              name: this.name.trim(),
              ...(this.ownerMode === 'me'
                ? { owner_type: 'User', owner_id: this.currentUser.id }
                : { kaption_assistant_id: this.kaptionAssistantId }),
              scopes: this.selectedScopes,
              roles: this.selectedRoles,
              ...(this.ownerMode === 'me'
                ? {
                    all_accounts: this.allAccounts,
                    account_ids: this.allAccounts
                      ? []
                      : this.selectedAccountIds,
                  }
                : {}),
              expires_in: this.expiresIn,
            },
          }
        );

        this.createdToken = response;
        this.showTokenModal = true;

        useAlert(this.$t('KAPTION_ACCESS_TOKENS.ADD.API.SUCCESS_MESSAGE'));
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          this.$t('KAPTION_ACCESS_TOKENS.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingAssistants || uiFlags.fetchingScopes"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_ACCESS_TOKENS.ADD.TITLE')"
        :description="$t('KAPTION_ACCESS_TOKENS.ADD.DESCRIPTION')"
        :back-button-label="$t('KAPTION_ACCESS_TOKENS.HEADER')"
        show-back-button
        @back="navigateBack"
      />
    </template>
    <template #body>
      <form class="max-w-2xl space-y-6" @submit.prevent="createToken">
        <!-- Token Name -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="name"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            :placeholder="$t('KAPTION_ACCESS_TOKENS.FORM.NAME.PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.NAME.HELP') }}
          </p>
        </div>

        <!-- Kaption Assistant Selection -->
        <!-- Owner -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.OWNER.LABEL') }}
          </label>
          <div class="flex gap-4 text-sm text-n-slate-12">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input v-model="ownerMode" type="radio" value="me" />
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.OWNER.ME') }}
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input v-model="ownerMode" type="radio" value="assistant" />
              {{ $t('KAPTION_ACCESS_TOKENS.FORM.OWNER.ASSISTANT') }}
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.OWNER.HELP') }}
          </p>
        </div>

        <!-- Workspaces (personal keys only) -->
        <div v-if="ownerMode === 'me'" class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ACCOUNTS.LABEL') }}
          </label>
          <div class="border border-n-weak rounded-lg divide-y divide-n-weak">
            <label
              class="flex items-start gap-2 px-3 py-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <input v-model="allAccounts" type="checkbox" class="mt-1" />
              <span class="flex flex-col">
                <span>{{
                  $t('KAPTION_ACCESS_TOKENS.FORM.ACCOUNTS.ALL', {
                    count: accounts.length,
                  })
                }}</span>
                <span class="text-xs text-n-slate-11">{{
                  $t('KAPTION_ACCESS_TOKENS.FORM.ACCOUNTS.ALL_HELP')
                }}</span>
              </span>
            </label>
            <label
              v-for="account in accounts"
              :key="account.id"
              class="flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12"
              :class="allAccounts ? 'opacity-50' : 'cursor-pointer'"
            >
              <input
                type="checkbox"
                :checked="
                  allAccounts || selectedAccountIds.includes(account.id)
                "
                :disabled="allAccounts"
                @change="toggleAccount(account.id)"
              />
              {{ accountLabel(account) }}
            </label>
          </div>
        </div>

        <!-- Roles -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ROLES.LABEL') }}
          </label>
          <div class="border border-n-weak rounded-lg divide-y divide-n-weak">
            <label
              v-for="role in roleRows"
              :key="role.key"
              class="flex items-start gap-2 px-3 py-2 cursor-pointer"
            >
              <input
                type="checkbox"
                class="mt-1"
                :checked="selectedRoles.includes(role.key)"
                @change="toggleRole(role.key)"
              />
              <span class="flex flex-col">
                <span class="text-sm text-n-slate-12">{{ role.label }}</span>
                <span class="text-xs text-n-slate-11">{{
                  role.description
                }}</span>
              </span>
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ROLES.HELP') }}
          </p>
        </div>

        <div v-if="ownerMode === 'assistant'" class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.LABEL') }}
          </label>
          <KaptionAssistantSelector
            :assistants="kaptionAssistants"
            :selected-id="kaptionAssistantId"
            @select="onAssistantSelect"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.ASSISTANT.HELP') }}
          </p>
        </div>

        <!-- Scope Selection -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.LABEL') }}
          </label>
          <ScopeSelector
            :scopes="scopes"
            :selected-scopes="selectedScopes"
            @change="onScopesChange"
          />
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.SCOPES.HELP') }}
          </p>
        </div>

        <!-- Expiration -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_ACCESS_TOKENS.FORM.EXPIRES_IN.LABEL') }}
          </label>
          <select
            v-model="expiresIn"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent"
          >
            <option
              v-for="option in expirationOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <NextButton
            blue
            :label="$t('KAPTION_ACCESS_TOKENS.FORM.SUBMIT')"
            :disabled="!isFormValid"
            :is-loading="uiFlags.creatingItem"
            type="submit"
          />
          <NextButton
            slate
            faded
            :label="$t('KAPTION_ACCESS_TOKENS.FORM.CANCEL')"
            @click="navigateBack"
          />
        </div>
      </form>
    </template>

    <TokenCreatedModal
      v-if="showTokenModal"
      :token="createdToken"
      @close="hideTokenModal"
    />
  </SettingsLayout>
</template>
